import { createUseStyles } from 'react-jss';
import {
  AbsolutesBlackColors,
  AbsolutesWhiteColors,
  PrimaryColors,
  InformationColors,
} from '@picsart/design-system/foundation/colors';
import { mediaQuery } from '@picsart/rc/helpers';
import type { DescriptionProps } from '@picsart/growth-rc/Description';
import { FontSizes, LineHeights } from '@picsart/design-system/foundation/typography';
import { Spacings } from '@picsart/design-system/foundation/spacings';
import { BreakPoints } from '@picsart/design-system/foundation/responsive';

import { ColorSchemes } from 'redesignedComponents/context/colorScheme/enums';
import type { ColorSchemeType } from 'redesignedComponents/context/colorScheme/types';
import type { CTAProps } from 'redesignedComponents/organisms/CTA';

import { CopyAlignment, Layout, LayoutOrder, Padding } from './enums';

type StyleProps = {
  colorScheme?: ColorSchemeType;
  layout?: Layout;
  layoutOrder?: LayoutOrder;
  copyAlignment?: CopyAlignment;
  hasBackground?: boolean;
  isMobile?: boolean;
  imageBleed?: boolean;
  description?: DescriptionProps;
  widgetWithMedia?: string | number | boolean;
  isHomePage?: boolean;
  secondaryCta?: CTAProps;
  isDefaultHeight?: boolean;
  padding?: Padding;
  isSecondaryOrHyperLink?: boolean;
};

const useStyles = createUseStyles<string, StyleProps>(
  () => {
    return {
      root: {
        position: 'relative',
        display: 'flex',
        flex: 1,
        background: ({ colorScheme, hasBackground }: StyleProps) => {
          if (hasBackground) return 'transparent';

          return colorScheme === ColorSchemes.DARK
            ? AbsolutesBlackColors.base.default
            : AbsolutesWhiteColors.base.default;
        },
        color: ({ colorScheme }: StyleProps) =>
          colorScheme === ColorSchemes.DARK
            ? AbsolutesBlackColors.text.default
            : AbsolutesWhiteColors.text.default,
        minHeight: 320,
      },
      logo: {
        height: 80,
        width: 200,
      },
      content: {
        display: 'flex',
        minWidth: 0,
        flex: 1,
        paddingBlock: ({ padding }) => Spacings[padding],
        margin: [Spacings.s0, 'auto'],
        flexDirection: ({ layout, layoutOrder, isMobile }: StyleProps) => {
          if (isMobile) return 'column-reverse';
          if (layout === Layout.Copy100) {
            return layoutOrder === LayoutOrder.Media_Copy ? 'row' : 'row-reverse';
          }
          return layoutOrder === LayoutOrder.Media_Copy ? 'row-reverse' : 'row';
        },
        [mediaQuery(BreakPoints.Small)]: {
          paddingBottom: Spacings.s12,
        },
      },
      copy: {
        display: 'flex',
        flexDirection: 'column',
        zIndex: 2,
        flex: 1,
        paddingLeft: ({ isHomePage }) => isHomePage && 0,
        alignItems: ({ isMobile, widgetWithMedia, copyAlignment, layout }) =>
          isMobile ||
          widgetWithMedia ||
          (copyAlignment === CopyAlignment.Center && layout === Layout.Copy100)
            ? 'center'
            : 'unset',
        '& > *': {
          paddingInline: ({ layoutOrder }) => {
            return layoutOrder === LayoutOrder.Media_Copy ? Spacings.s56 : `0 ${Spacings.s56}`;
          },
        },
        '@media screen and (max-width: 1200px)': {
          '&&': {
            '& > *': {
              paddingInline: Spacings.s0,
            },
          },
        },
        justifyContent: ({ copyAlignment }: StyleProps) => {
          switch (copyAlignment) {
            case CopyAlignment.SpaceAround:
              return 'space-around';
            case CopyAlignment.SpaceBetween:
              return 'space-between';
            case CopyAlignment.Center:
              return 'center';
            default:
              return 'flex-start';
          }
        },
        textAlign: ({ copyAlignment, layout }: StyleProps) =>
          copyAlignment === CopyAlignment.Center && layout === Layout.Copy100 ? 'center' : 'unset',
      },
      copyTitle: {
        paddingBottom: ({ isMobile, copyAlignment }: StyleProps) => {
          if (isMobile) return Spacings.s8;
          return [CopyAlignment.Top, CopyAlignment.Center].includes(copyAlignment)
            ? Spacings.s16
            : Spacings.s0;
        },
        '& > span s': {
          color: ({ colorScheme }) =>
            colorScheme === ColorSchemes.DARK
              ? InformationColors.base.default
              : PrimaryColors.base.default,
          textDecoration: 'none',
        },
        textAlign: ({ isMobile }) => (isMobile ? 'center' : 'unset'),
      },
      copyItem: {
        paddingBottom: ({ isMobile, copyAlignment, isSecondaryOrHyperLink }: StyleProps) => {
          if (isMobile || isSecondaryOrHyperLink) return Spacings.s16;
          return [CopyAlignment.Top, CopyAlignment.Center].includes(copyAlignment)
            ? Spacings.s32
            : Spacings.s0;
        },
        textAlign: ({ isMobile }) => (isMobile ? 'center' : 'unset'),
      },
      secondaryCopyItem: {
        '& > a': {
          width: '100%',
        },
      },
      ctaHolder: ({ secondaryCta, isMobile, description }) => ({
        display: secondaryCta ? 'flex' : 'initial',
        flexDirection: isMobile ? 'column' : 'row',
        marginTop: !description ? Spacings.s16 : 'unset',
        columnGap: Spacings.s24,
        '@media screen and (max-width: 768px)': {
          width: secondaryCta ? 'initial' : '100%',
        },
      }),
      mediaWrapper: {
        zIndex: 1,
        display: 'flex',
        alignItems: 'center',
        height: '100%',
        position: ({ layout }: StyleProps) => (layout === Layout.Copy100 ? 'absolute' : 'relative'),
        flex: ({ layout }: StyleProps) => (layout === Layout.Copy25_Media75 ? 3 : 1),
        minWidth: 0,
      },
      media: ({ imageBleed, layoutOrder, isDefaultHeight }: StyleProps) => ({
        '&&': {
          display: 'flex',
          justifyContent: 'center',
          position: imageBleed ? 'absolute' : 'initial',
          width: imageBleed ? 'auto' : '100%',
          height: '100%',
          right: imageBleed && layoutOrder === LayoutOrder.Copy_Media ? 0 : 'auto',
          left: imageBleed && layoutOrder === LayoutOrder.Media_Copy ? 0 : 'auto',
          overflow: 'visible',
          '& > div': {
            width: '100%',
            '& img': {
              // [mediaQuery(768)]: {
              '@media screen and (max-width: 768px)': {
                maxHeight: isDefaultHeight && 460,
              },
            },
          },
        },

        // TODO Fix the issue in Growth RC and remove this block
        '& > video': {
          opacity: '1 !important',
        },
      }),
      secondDescription: {
        [mediaQuery(1023)]: {
          display: 'flex',
          justifyContent: 'center',
          flexDirection: 'column',
        },

        '& p': {
          textAlign: 'left',
          fontSize: FontSizes.s14,
          lineHeight: LineHeights.l20,

          [mediaQuery(1023)]: {
            maxWidth: 360,
            textAlign: 'center',
            fontSize: FontSizes.s12,
          },
        },
      },
      skeletonRoot: {
        extend: 'root',
      },
      skeletonContent: {
        minWidth: '100%',
      },
    };
  },
  {
    name: 'banner-block',
  },
);

export default useStyles;
